import { injectScript } from '~/utils/dom';

// New Google Identity Services implementation
var loading: Promise<boolean> | undefined;
var ready: boolean = false;

const SCRIPT_ID = "gsi-script";
const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

export function loadGoogleIdentity (): Promise<boolean> {
  // Check if Google Identity Services is already loaded
  if (window?.google?.accounts?.id) {
    return Promise.resolve(true);
  }

  const initGoogleIdentity = (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      try {
        // Google Identity Services is ready
        if (window?.google?.accounts?.id) {
          resolve(true);
        } else {
          reject(new Error('Google Identity Services failed to load'));
        }
      } catch (err) {
        console.error('Google Identity Services initialization error:', err);
        reject(err);
      }
    });
  };

  if (ready) {
    return initGoogleIdentity();
  }

  // Load the script if not already loading
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      // Inject the new Google Identity Services SDK
      const el = injectScript(SCRIPT_ID, SCRIPT_SRC);
      el.onload = () => {
        ready = true;
        resolve(initGoogleIdentity());
      };
      el.onerror = () => {
        reject(new Error('Failed to load Google Identity Services script'));
      };
    });
  }
  return loading;
}

// using new Google Identity Services
export async function initializeGoogleSignIn (config: Types.GoogleIdentityConfig): Promise<void> {
  await loadGoogleIdentity();

  // Initialize Google Identity Services
  if (window.google?.accounts?.id) {
    window.google.accounts.id.initialize({
      client_id: config.client_id,
      callback: config.callback,
      auto_select: config.auto_select || false,
      cancel_on_tap_outside: config.cancel_on_tap_outside !== false,
    });
  }
}

export function renderGoogleSignInButton (elementId: string, options?: Types.GoogleSignInButtonOptions): void {
  if (!window?.google?.accounts?.id) {
    throw new Error("Google Identity Services not loaded. Call initializeGoogleSignIn first.");
  }

  const element = document.getElementById(elementId);
  if (element) {
    window.google.accounts.id.renderButton(element, {
      theme: options?.theme || 'outline',
      size: options?.size || 'large',
      type: options?.type || 'standard',
      shape: options?.shape || 'rectangular',
      width: options?.width || 200,
      ...options,
    });
  }
}

export function showOneTap (): void {
  if (!window?.google?.accounts?.id) {
    throw new Error("Google Identity Services not loaded. Call initializeGoogleSignIn first.");
  }

  window.google.accounts.id.prompt();
}

export function signOutGoogle (): void {
  if (window?.google?.accounts?.id) {
    window.google.accounts.id.disableAutoSelect();
  }
}

// Helper to decode JWT token
export function decodeJWT (token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}
